import { SpanDirection } from "building-types/floors/floorComponentsTypes";
import type { RotatedPolygonBoundingBox2 } from "building-types/framing/framingGeneratorTypes";
import { aabb2Area, aabb2ByPolyLine2s } from "geom/aabb2";
import { DIST_EPSILON } from "geom/core";
import { offsetPolyLine2 } from "geom/offset";
import {
  polyLine2Area,
  polyLine2Copy,
  polyLine2CopySimplify,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2IsAxisAlignedRect,
  polyLine2Rotate,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import { framingDirectionVector, framingPerpendicularVector } from "./framingLayoutUtils";

export class FramingGeneratorBase {
  private memberIdCounter: number;

  protected constructor(memberIdStartOffset: number = 0) {
    this.memberIdCounter = memberIdStartOffset;
  }

  protected generateMemberId(): string {
    return `member-${this.memberIdCounter++}`;
  }

  protected framingDirectionAndPerpDirectionVecs(
    direction: number | SpanDirection,
    perimeter: PolyLine2,
  ): { framingDirectionVec: Vec2; framingPerpDirectionVec: Vec2 } {
    let framingDirectionAngle: number | undefined = typeof direction === "number" ? direction : undefined;
    let framingDirectionVec: Vec2 = [1, 0];
    let framingPerpDirectionVec: Vec2 = [0, 1];

    if (framingDirectionAngle === undefined) {
      const optimalBB = this.computeOptimalBoundingBox([perimeter]);
      const diffX = optimalBB.maxX - optimalBB.minX;
      const diffY = optimalBB.maxY - optimalBB.minY;
      framingDirectionAngle = -optimalBB.rotationAngleRadians + Math.PI / 2;
      if (
        (diffX < diffY && direction === SpanDirection.SHORTEST_SPAN) ||
        (diffX > diffY && direction === SpanDirection.LONGEST_SPAN)
      ) {
        framingDirectionAngle = -optimalBB.rotationAngleRadians;
      }
    }

    if (framingDirectionAngle !== undefined) {
      framingDirectionVec = framingDirectionVector(framingDirectionAngle);
      framingPerpDirectionVec = framingPerpendicularVector(framingDirectionAngle);
    }

    return { framingDirectionVec, framingPerpDirectionVec };
  }

  protected computeOptimalBoundingBox(polyline: PolyLine2[], resize: number = 0): RotatedPolygonBoundingBox2 {
    const pl =
      resize === 0 ? polyLine2CopySimplify(polyline[0]) : polyLine2CopySimplify(offsetPolyLine2(polyline[0], -resize));
    const openings = polyline.slice(1);
    const polyArea = polyLine2Area(pl);
    let aabb = aabb2ByPolyLine2s([pl]);
    if (!aabb) {
      throw new Error("Failed to compute bounding box");
    }
    let diffArea = aabb2Area(aabb) - polyArea;

    let rotatedBoundingBox: RotatedPolygonBoundingBox2 = {
      ...aabb,
      rotationAngleRadians: 0,
      areaDifference: diffArea,
      rotatedPerimeter: pl,
      rotatedOpenings: openings.map((opening) => polyLine2CopySimplify(opening)),
    };

    if (!polyLine2IsAxisAlignedRect(pl)) {
      for (let i = 0, l = polyLine2EdgeCount(pl); i < l; i++) {
        const edge = polyLine2Edge(pl, i);
        const edgeAngle = Math.atan2(edge[3] - edge[1], edge[2] - edge[0]);
        const rotatedPerimeter = polyLine2Rotate(polyLine2Copy(pl), -edgeAngle, 0, 0);
        aabb = aabb2ByPolyLine2s([rotatedPerimeter]);
        if (!aabb) {
          continue;
        }
        diffArea = aabb2Area(aabb) - polyArea;

        const rotatedOpenings: PolyLine2[] = [];
        for (const opening of openings) {
          const resizedOpening =
            resize === 0 ? polyLine2CopySimplify(opening) : polyLine2CopySimplify(offsetPolyLine2(opening, resize));
          rotatedOpenings.push(polyLine2Rotate(resizedOpening, -edgeAngle, 0, 0));
        }

        if (diffArea < rotatedBoundingBox.areaDifference - DIST_EPSILON) {
          rotatedBoundingBox = {
            ...aabb,
            rotationAngleRadians: edgeAngle,
            areaDifference: diffArea,
            rotatedPerimeter,
            rotatedOpenings,
          };
        }
      }
    }
    return rotatedBoundingBox;
  }
}
